<template>
  <div class="dropdown" v-loading="loading">
    <div class="user-wrapper">
      <Avatar
        :nickname="userStore.nickName"
        :company-user="userInfo.companyUser"
        :can-edit="false"
        :color="userInfo.color"
        :src="userInfo.headerImg"
        :size="46"></Avatar>
      <div class="user-info">
        <p class="nickname text-overflow">{{ nickname }}</p>
        <p class="email text-overflow">{{ userInfo.email || userInfo.phone || userInfo.account }}</p>
      </div>
    </div>
    <p class="plan-list" v-if="userPlans.length">
      <span v-for="item in userPlans" :key="item.planId" :class="item.className">{{ item.productName }}</span>
    </p>
    <p class="divider"></p>
    <button class="button-item" @click="handleAction('accountCenterShow')" :class="{ hide: props.ignoreActions.includes('accountCenter') }">
      <img class="icon" src="@/assets/icon-user-center.svg" v-svg-inline />
      {{ $t('Account center') }}
    </button>
    <button class="button-item" @click="handleAction('feedbackShow')" :class="{ hide: props.ignoreActions.includes('feedback') }">
      <img class="icon" src="@/assets/icon-feedback.svg" v-svg-inline />
      {{ $t('Feedback') }}
    </button>
    <button class="button-item" @click="handleAction('gotoDownload')" :class="{ hide: props.ignoreActions.includes('download') }">
      <img class="icon" src="@/assets/icon-download.svg" v-svg-inline />
      {{ $t('Get our desktop/mobile app') }}
    </button>
    <p class="divider" :class="{ hide: props.ignoreActions.includes('logout') }"></p>
    <button class="button-item danger" @click="handleAction('logOut')" :class="{ hide: props.ignoreActions.includes('logout') }">
      <img class="icon" src="@/assets/icon-logout.svg" v-svg-inline />
      {{ $t('Log Out') }}
    </button>
  </div>
  <Teleport to="body">
    <el-dialog v-model="feedbackDialogVisible" class="no-header" style="max-width: 420px;" v-if="!appStore.isIframe">
      <Feedback @on-close="feedbackDialogVisible = false"></Feedback>
    </el-dialog>
  </Teleport>
  
</template>

<script setup>
import { getUserBasePlanList, getUserInfo, logOut } from '@/api/user/user';
import Avatar from '@/components/user/avatar.vue';
import { postMessageToOpenerWindow } from '@/libs/utils';
import { uniqWith } from 'lodash';
import { computed, onMounted, reactive, ref } from 'vue';
import Feedback from './feedback.vue';
import { appStore, userStore } from '@/stores';
import enums from '@/libs/enums';
import { logOutSuccess } from '@/services/login';
import { isLogin } from '@/services/base';
const userInfo = computed(() => userStore.userInfo)
const userPlans = reactive([])
const loading = ref(false)

const feedbackDialogVisible = ref(false)

const props = defineProps({
  // 需要排出的操作项: accountCenter, feedback, download, logout
  ignoreActions: {
    type: Array,
    default: () => [],
  }
})

const emits = defineEmits(['on-action'])

const nickname = computed(() => {
  if (userInfo.value?.firstName || userInfo.value?.lastName) {
    return `${userInfo.value?.firstName} ${userInfo.value?.lastName}`
  }
  return userInfo.value?.nickName
})

const handleGetUserInfo = async () => {
  try {
    const { data } = await getUserInfo()
    Object.assign(userStore.userInfo, data.userInfo)
  } catch (e) {
    console.log(e)
  }
}

const handleGetUserBasePlan = async () => {
  try {
    const { data } = await getUserBasePlanList()
    userPlans.splice(0, userPlans.length)
    data.forEach(item => {
      if (item.productType.includes('AI')) {
        item.className = 'ai'
        item.productName = 'UPDF AI'
      } else if (item.productType.includes('Sign')) {
        item.className = 'sign'
        item.productName = 'UPDF Sign'
      } else {
        item.className = 'updf'
        item.productName = 'UPDF Pro'
      }
    })
    // 可能有多个相同类型的plan，需要去重处理
    const uniqData = uniqWith(data, (item1, item2) => {
      return item1.className === item2.className
    })
    userPlans.push(...uniqData)
  } catch (e) {
    console.log(e)
  }
}

const handleAction = async (action) => {
  postMessageToOpenerWindow('profileDropdownHide')
  emits('on-action')
  if (action === 'logOut') {
    logOut().finally(() => {
      logOutSuccess(false)
      postMessageToOpenerWindow('logOut')
    })
  } else if (action === 'accountCenterShow') {
    postMessageToOpenerWindow('accountCenterShow')
  } else if (action === 'feedbackShow') {
    if (!appStore.isIframe) {
      feedbackDialogVisible.value = true
    }
    postMessageToOpenerWindow('feedbackShow')
  } else if (action === 'gotoDownload') {
    window.open(enums.websiteUrl.downloadLink, '_blank')
  }
}

// 通知父窗口下拉大小
const handlePostWrapperSize = () => {
  const wrapper = document.querySelector('.dropdown')
  const { width, height } = wrapper?.getBoundingClientRect() || { width: 0, height: 0 }
  postMessageToOpenerWindow('profileDropdownReSize', {
    width,
    height
  })
}

onMounted(async() => {
  if (isLogin()) {
    loading.value = true
    await handleGetUserInfo()
    await handleGetUserBasePlan()
    loading.value = false
    setTimeout(() => {
      handlePostWrapperSize()
    }, 200)
  }
  if (appStore.isIframe) {
    document.body.classList.add('is-iframe')
  }
})
</script>

<style lang="scss" scoped>
.hide {
  display: none !important;
}
.dropdown {
  width: 300px;
  padding: 8px;
  box-sizing: border-box;
  background: var(--BG-0, #FFFFFF);
  border-radius: 12px;
  overflow: hidden;
  .user-wrapper {
    padding: 8px;
    padding-bottom: 12px;
    display: flex;
    align-items: center;
    .user-info {
      margin-left: 12px;
      flex: 1;
      overflow: hidden;
      .nickname {
        color: var(--BG-10, #222222);
        font-size: 14px;
        line-height: 17px;
        font-weight: bold;
      }
      .email {
        font-size: 14px;
        line-height: 17px;
        margin-top: 4px;
        color: var(--BG-6hint-text, #707070);
      }
    }
  }
  .plan-list {
    padding: 0px 8px 17px;
    span {
      font-size: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 3.5px 8px;
      border-radius: 6px;
      margin-right: 8px;
      &.updf {
        background: #2689FD1A;
        color: var(--Color-3, #2689FD);
      }
      &.sign {
        background: #AD46FF1A;
        color: var(--Color-2, #AD46FF);
      }
      &.ai {
        background: #7479FF1A;
        color: var(--Color-1, #7479FF);
      }
    }
  }
  .divider {
    width: calc(100% - 16px);
    margin: 0 auto;
    border-top: 0.5px solid var(--BG-3, #DDDDDD);
  }
  .button-item {
    height: 44px;
    width: 100%;
    display: flex;
    align-items: center;
    text-align: left;
    padding: 12px;
    box-sizing: border-box;
    border-radius: 8px;
    border: none;
    background: none;
    cursor: pointer;
    transition: all .3s;
    color: var(--BG-10, #222222);
    .icon {
      margin-right: 8px;
    }
    &:hover {
      background: var(--BG-2, #EAEAEA);
      &.danger {
        background: #F9535333;
        color: var(--Alert, #F95353);
        :deep(path), :deep(circle) {
          stroke: var(--Alert, #F95353) !important;
        } 
      }
    }
    &:active:not(.danger) {
      background: var(--BG-3, #DDDDDD);
    }
  }
}
</style>

<style lang="scss">
.ar-SA {
  .dropdown {
    .user-wrapper {
      .user-info {
        margin-left: 0;
        margin-right: 12px;
      }
    }
    .button-item {
      text-align: right;
      .icon {
        margin-right: 0;
        margin-left: 8px;
      }
    }
  }
}
body {
  &.is-iframe {
    overflow: hidden;
    #app {
      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
    }
  }
}
</style>